import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Linkedin, Send, User } from 'lucide-react';
import emailjs from 'emailjs-com';
import { useRef } from 'react';


// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: (delay = 0) => ({
    opacity: 1,
    transition: { delay, duration: 0.6, ease: 'easeOut' },
  }),
};

function Contact() {
  const formRef = useRef();

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(
        'service_38j0u8m',       // e.g. service_123abc
        'template_ibxrapy',      // e.g. template_xzy987
        formRef.current,
        '_mKvNnBJByzdGV1XJ'        // e.g. Rk9P0QjX4-abc123
      )
      .then(
        (result) => {
          console.log('Message sent:', result.text);
          alert('Message sent successfully!');
        },
        (error) => {
          console.log('Error:', error.text);
          alert('Something went wrong. Please try again.');
        }
      );

    e.target.reset();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Header */}
      <motion.div
        className="relative overflow-hidden bg-gradient-to-r from-purple-700 to-pink-700"
        initial="hidden"
        animate="show"
        variants={fadeInUp}
      >
        <div className="px-6 py-16 sm:px-8 lg:px-12 text-center">
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-6 backdrop-blur-sm"
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
          >
            <User className="w-10 h-10 text-white" />
          </motion.div>
          <motion.h1 className="text-4xl md:text-6xl font-bold mb-4" variants={fadeInUp}>
            Get In Touch
          </motion.h1>
          <motion.p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto" variants={fadeInUp} transition={{ delay: 0.3 }}>
            Let's connect and discuss opportunities, collaborations, or just have a friendly conversation.
          </motion.p>
        </div>
      </motion.div>

      {/* Content */}
      <div className="px-6 py-16 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Info */}
            <motion.div
              className="space-y-8"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <motion.div className="text-center lg:text-left" variants={fadeInUp}>
                <h2 className="text-3xl font-bold mb-4">Contact Information</h2>
                <p className="text-lg text-slate-300">
                  Ready to start a conversation? Here are the best ways to reach me.
                </p>
              </motion.div>

              {[
                {
                  icon: <MapPin className="w-6 h-6 text-purple-400" />,
                  title: 'Address',
                  content: (
                    <>
                      House 8, Sunshine Estate<br />
                      Destiny Homes, Abijo<br />
                      Lagos, Nigeria
                    </>
                  ),
                  delay: 0.2,
                },
                {
                  icon: <Phone className="w-6 h-6 text-green-400" />,
                  title: 'Phone',
                  content: (
                    <a href="tel:+2347055108827" className="hover:text-green-400 transition-colors">
                      +234 705 510 8827
                    </a>
                  ),
                  delay: 0.3,
                },
                {
                  icon: <Mail className="w-6 h-6 text-purple-400" />,
                  title: 'Email',
                  content: (
                    <a href="mailto:erodaniel310@gmail.com" className="hover:text-purple-400 transition-colors">
                      erodaniel310@gmail.com
                    </a>
                  ),
                  delay: 0.4,
                },
                {
                  icon: <Linkedin className="w-6 h-6 text-blue-400" />,
                  title: 'LinkedIn',
                  content: (
                    <a
                      href="https://www.linkedin.com/in/ero-daniel-0b52a9271/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-400 transition-colors"
                    >
                      Connect on LinkedIn
                    </a>
                  ),
                  delay: 0.5,
                },
              ].map((info, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-4 p-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl"
                  custom={info.delay}
                  variants={fadeIn}
                >
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">{info.icon}</div>
                  <div>
                    <h3 className="font-semibold mb-1">{info.title}</h3>
                    <p className="text-slate-300">{info.content}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <form ref={formRef} onSubmit={sendEmail} className="space-y-6">
  <div className="grid sm:grid-cols-2 gap-6">
    <input
      name="first_name"
      type="text"
      placeholder="First Name"
      required
      className="w-full px-4 py-3 bg-white/10 text-white placeholder-slate-400 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500"
    />
    <input
      name="last_name"
      type="text"
      placeholder="Last Name"
      required
      className="w-full px-4 py-3 bg-white/10 text-white placeholder-slate-400 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500"
    />
  </div>

  <input
    name="user_email"
    type="email"
    placeholder="Email Address"
    required
    className="w-full px-4 py-3 bg-white/10 text-white placeholder-slate-400 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500"
  />

  <input
    name="subject"
    type="text"
    placeholder="Subject"
    className="w-full px-4 py-3 bg-white/10 text-white placeholder-slate-400 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500"
  />

  <textarea
    name="message"
    rows={5}
    placeholder="Your Message..."
    required
    className="w-full px-4 py-3 bg-white/10 text-white placeholder-slate-400 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 resize-none"
  ></textarea>

  <motion.button
    type="submit"
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-4 px-6 rounded-xl hover:shadow-lg transition-all"
  >
    Send Message
  </motion.button>
</form>

          </div>

          {/* CTA */}
          <motion.div
            className="mt-16 text-center"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-md border border-white/20 rounded-3xl p-12 text-white relative overflow-hidden">
              <h3 className="text-3xl font-bold mb-4">Ready to Start a Conversation?</h3>
              <p className="text-lg mb-6 max-w-2xl mx-auto">
                Whether you have a project in mind or just want to say hello, I'm always open to meaningful connections.
              </p>
              <a
                href="mailto:erodaniel310@gmail.com"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold px-8 py-4 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <Mail className="w-5 h-5" />
                <span>Start the Conversation</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
